// Home "Your week": a free, in-app stand-in for a weekly progress email.
// Everything is derived from the stored completion records (`completedAt` on units, Support Desk
// tickets and the practice ticket) — nothing is stored or synced for it, and it never unlocks or
// awards anything. XP per record comes from src/core/xp.js, so the totals match the rest of the site.
// Admin sandbox / bypass records (test or validated:false) are never counted.
import { unitXp } from './xp.js';
import { solvedDays } from './daily.js';
import { missionKind } from '../map/mapData.js';
import { SUPPORT_TICKETS, PRACTICE_TICKET } from '../domains/support/tickets.js';

const DAY = 86400000;
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Local midnight of the day containing `t`. */
export const dayStart = (t) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
/** Local Monday 00:00 of the week containing `t`. */
export function weekStart(t) {
  const d = new Date(dayStart(t));
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.getTime();
}
const addDays = (t, n) => { const d = new Date(t); d.setDate(d.getDate() + n); return d.getTime(); };
const real = (r) => r && r.status === 'complete' && Number.isFinite(r.completedAt) && !r.test && !r.bypass && r.validated !== false;

/** Every verified completion with a timestamp: { at, xp, title, kind: 'lab'|'ticket'|'practice', hints?, missionKind?, key? }. */
export function completions({ progress, curriculum, support }) {
  const out = [];
  if (progress && curriculum) {
    for (const d of curriculum.domains) {
      for (const u of d.units) {
        if (!u.implemented || progress.unitState(d.id, u.id) !== 'complete') continue;
        const rec = { status: 'complete', ...(progress.unitRecord?.(d.id, u.id) || {}) };
        if (!real(rec)) continue;
        out.push({ at: rec.completedAt, xp: unitXp(missionKind(u), rec), title: u.title, kind: 'lab', hints: Math.max(0, Number(rec.hints) || 0), missionKind: missionKind(u), key: `${d.id}.${u.id}` });
      }
    }
  }
  if (support) {
    for (const t of SUPPORT_TICKETS) {
      const rec = support.ticketRecord(t.id);
      if (real(rec)) out.push({ at: rec.completedAt, xp: 100, title: t.title, kind: 'ticket' });
    }
    const p = support.practiceRecord?.();
    if (real(p)) out.push({ at: p.completedAt, xp: 0, title: PRACTICE_TICKET.title, kind: 'practice' });
  }
  return out.sort((a, b) => b.at - a.at);
}

/** Consecutive days (ending today, or yesterday if nothing yet today) with at least one completion. */
export function dayStreak(list, now) {
  const days = new Set(list.map((c) => dayStart(c.at)));
  let day = dayStart(now);
  const today = days.has(day);
  if (!today) day = addDays(day, -1);
  let n = 0;
  while (days.has(day)) { n++; day = addDays(day, -1); }
  return { days: n, today };
}

/**
 * This week (Monday–Sunday, local time) at a glance.
 * @returns {{ start:number, end:number, xp:number, lastWeekXp:number, done:number, days:{label:string,xp:number,done:number,isToday:boolean,future:boolean}[], streak:{days:number,today:boolean}, recent:{title:string,xp:number,kind:string,at:number}[] }}
 */
export function weekSummary({ progress, curriculum, support, now = Date.now(), daily = null }) {
  const all = completions({ progress, curriculum, support });
  const start = weekStart(now);
  const end = addDays(start, 7);
  const prev = addDays(start, -7);
  const week = all.filter((c) => c.at >= start && c.at < end);
  const today = dayStart(now);
  const days = DAY_LABELS.map((label, i) => {
    const from = addDays(start, i); const to = addDays(start, i + 1);
    const hits = week.filter((c) => c.at >= from && c.at < to);
    return { label, xp: hits.reduce((s, c) => s + c.xp, 0), done: hits.length, isToday: from === today, future: from > today };
  });
  return {
    start, end,
    xp: week.reduce((s, c) => s + c.xp, 0),
    lastWeekXp: all.filter((c) => c.at >= prev && c.at < start).reduce((s, c) => s + c.xp, 0),
    done: week.length,
    days,
    // the streak also counts days with the daily challenge solved (activity only, no XP)
    streak: dayStreak([...all, ...solvedDays(daily)], now),
    recent: week.slice(0, 3),
  };
}

/** "19 – 25 Oct" (or "28 Oct – 3 Nov" across months). */
export function weekRangeLabel(start) {
  const a = new Date(start); const b = new Date(addDays(start, 6));
  const m = (d) => d.toLocaleString('en-GB', { month: 'short' });
  return a.getMonth() === b.getMonth() ? `${a.getDate()} – ${b.getDate()} ${m(b)}` : `${a.getDate()} ${m(a)} – ${b.getDate()} ${m(b)}`;
}

export { DAY };
